package co.uk.sacursoftware.ssinventory.util

import co.uk.sacursoftware.ssinventory.data.remote.DatabaseConfig
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*

object StorageArea {
    const val DAILY = "daily"
    const val DELIVERIES = "deliveries"
    const val ORDERS = "orders"
    const val TRAINING = "training"
    const val TOOLS = "tools"
    const val SERVICES = "services"
    const val FILES = "files"
}

class FirebaseStorageService(
    private val httpClient: HttpClient = HttpClient(),
    private val bucketName: String = "ssinventory-3894a.firebasestorage.app",
    private val apiKey: String = "AIzaSyAztvBqggLz5KsYB0nfk-SZmYgz8v2iPWg"
) {
    /**
     * Uploads image bytes to `{storeName}/{area}/{filename}.jpg` in Firebase Storage.
     */
    suspend fun uploadImage(imageBytes: ByteArray, area: String): String {
        return uploadFile(imageBytes, area, "${getCurrentEpochMillis()}.jpg", ContentType.Image.JPEG)
    }

    suspend fun uploadFile(
        fileBytes: ByteArray,
        area: String,
        originalName: String,
        contentType: ContentType? = null
    ): String {
        val storeName = storageFolderName()
        val safeName = originalName
            .substringAfterLast('/')
            .substringAfterLast('\\')
            .replace(Regex("[^A-Za-z0-9._-]"), "_")
            .ifBlank { "file.bin" }
        val fileName = "$storeName/$area/${getCurrentEpochMillis()}_$safeName"
        val mime = contentType ?: contentTypeForFileName(safeName)

        val url = "https://firebasestorage.googleapis.com/v0/b/$bucketName/o?key=$apiKey&uploadType=media&name=$fileName"
        AppLogger.d("FirebaseStorage Upload Request: $url (Size: ${fileBytes.size} bytes)")

        try {
            val response: HttpResponse = httpClient.post(url) {
                contentType(mime)
                setBody(fileBytes)
            }

            val responseBody = response.bodyAsText()
            if (response.status.isSuccess()) {
                AppLogger.d("FirebaseStorage Upload Success: $responseBody")
                return "https://firebasestorage.googleapis.com/v0/b/$bucketName/o/${fileName.replace("/", "%2F")}?alt=media"
            } else {
                AppLogger.e("FirebaseStorage Upload Error: HTTP ${response.status.value} - $responseBody")
                throw Exception("Failed to upload file to Firebase Storage: HTTP ${response.status.value} - $responseBody")
            }
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Throwable) {
            AppLogger.e("FirebaseStorage Upload Exception: ${e.message}", e)
            throw Exception(e.message ?: "Upload failed", e)
        }
    }

    /**
     * Deletes an image from the Firebase Storage bucket using its public download URL via REST API.
     */
    suspend fun deleteOrderImage(imageUrl: String) {
        try {
            val pathPart = imageUrl.substringAfter("/o/").substringBefore("?").replace("%2F", "/")
            if (pathPart.isBlank()) {
                AppLogger.e("FirebaseStorage Delete Error: Blank path extracted from URL '$imageUrl'")
                return
            }

            val encodedPath = pathPart.replace("/", "%2F")
            val url = "https://firebasestorage.googleapis.com/v0/b/$bucketName/o/$encodedPath?key=$apiKey"
            AppLogger.d("FirebaseStorage Delete Request: $url")

            val response: HttpResponse = httpClient.delete(url)
            val responseBody = response.bodyAsText()

            if (response.status.isSuccess() || response.status == HttpStatusCode.NotFound) {
                AppLogger.d("FirebaseStorage Delete Success/NotFound: HTTP ${response.status.value}")
            } else {
                AppLogger.e("FirebaseStorage Delete Failed: HTTP ${response.status.value} - $responseBody")
            }
            ImageCache.evict(imageUrl)
        } catch (e: kotlin.coroutines.cancellation.CancellationException) {
            throw e
        } catch (e: Throwable) {
            AppLogger.e("FirebaseStorage Delete Exception for '$imageUrl': ${e.message}", e)
        }
    }
}

internal fun contentTypeForFileName(name: String): ContentType {
    val ext = name.substringAfterLast('.', "").lowercase()
    return when (ext) {
        "pdf" -> ContentType.Application.Pdf
        "png" -> ContentType.Image.PNG
        "jpg", "jpeg" -> ContentType.Image.JPEG
        "gif" -> ContentType.Image.GIF
        "webp" -> ContentType.parse("image/webp")
        "txt" -> ContentType.Text.Plain
        "csv" -> ContentType.Text.CSV
        "html", "htm" -> ContentType.Text.Html
        "mp4" -> ContentType.parse("video/mp4")
        "webm" -> ContentType.parse("video/webm")
        "mkv" -> ContentType.parse("video/x-matroska")
        "mov" -> ContentType.parse("video/quicktime")
        "mp3" -> ContentType.parse("audio/mpeg")
        "ppt" -> ContentType.parse("application/vnd.ms-powerpoint")
        "pptx" -> ContentType.parse("application/vnd.openxmlformats-officedocument.presentationml.presentation")
        "doc" -> ContentType.parse("application/msword")
        "docx" -> ContentType.parse("application/vnd.openxmlformats-officedocument.wordprocessingml.document")
        "xls" -> ContentType.parse("application/vnd.ms-excel")
        "xlsx" -> ContentType.parse("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
        else -> ContentType.Application.OctetStream
    }
}

internal fun storageFolderName(): String {
    val shopName = AppSession.userProfile
        ?.firstOrNull { it.type.equals("shopName", ignoreCase = true) }
        ?.data
        ?.trim()
        .orEmpty()
    val raw = shopName.ifBlank { DatabaseConfig.variant.ifBlank { "store" } }
    return raw.lowercase()
        .replace(Regex("[^a-z0-9]+"), "-")
        .trim('-')
        .ifBlank { "store" }
}
